// API Service - Repository Pattern
// Base API service with error handling and request interceptors

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('adminToken');

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'An error occurred' }));
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  // GET request
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  // POST request
  async post(endpoint, data = {}, options = {}) {
    // If data is FormData, don't stringify it
    if (data instanceof FormData) {
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('adminToken');
      
      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        },
        body: data,
      };
      
      try {
        console.log('POST Request URL:', url);
        console.log('POST Request Config:', { method: config.method, headers: config.headers });
        
        const response = await fetch(url, config);
        
        console.log('POST Response Status:', response.status);
        console.log('POST Response Headers:', Object.fromEntries(response.headers.entries()));
        
        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response URL:', url);
          console.error('Non-JSON response:', text.substring(0, 500));
          throw new Error(`Server returned non-JSON response. Status: ${response.status}. URL: ${url}. Check console for details.`);
        }
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ message: 'An error occurred' }));
          console.log('Error response body:', error);
          const errorMessage = error.message || (error.errors ? JSON.stringify(error.errors) : null) || `HTTP error! status: ${response.status}`;
          throw new Error(errorMessage);
        }
        return await response.json();
      } catch (error) {
        console.error('API Request Error:', error);
        throw error;
      }
    }
    
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // PUT request
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Upload file
  async upload(endpoint, formData) {
    const token = localStorage.getItem('adminToken');
    const url = `${this.baseURL}${endpoint}`;

    const config = {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Upload Error:', error);
      throw error;
    }
  }
}

export default new ApiService();
